/**
 * LineBasedFOV - מחלקה לחישוב שדה ראייה (FOV) על בסיס קטעים מדויקים
 * 
 * מימוש אלגוריתם ראייה מבוסס הטלת קרניים (Ray Casting) ויצירת פוליגון ראייה
 */

class LineBasedFOV {
  /**
   * יוצר אובייקט חדש לחישוב שדה ראייה מבוסס קטעים
   * @param {Object} options - אפשרויות אתחול
   * @param {number} options.rayCount - מספר הקרניים שיוטלו (ברירת מחדל: 720)
   * @param {number} options.maxDistance - מרחק הראייה המקסימלי (ברירת מחדל: 1000)
   * @param {boolean} options.enableCulling - האם להפעיל culling לקטעים מחוץ למסך (ברירת מחדל: true)
   * @param {number} options.cullMargin - השוליים להוספה לחלון בעת culling (ברירת מחדל: 200)
   * @param {boolean} options.enableDebug - האם להפעיל מצב דיבאג (ברירת מחדל: false)
   * @param {boolean} options.useWorker - האם להשתמש בעובד נפרד לחישובים כבדים (ברירת מחדל: false)
   */
  constructor(options = {}) {
    this.rayCount = options.rayCount || 720;
    this.maxDistance = options.maxDistance || 1000;
    this.walls = []; // מערך הקטעים החוסמים ראייה
    this.specialObjects = []; // אובייקטים עם חוקי LOS מיוחדים
    this.visibilityPolygon = []; // מערך נקודות פוליגון הראייה (שטוח)
    this.origin = { x: 0, y: 0 }; // נקודת המוצא לחישוב שדה הראייה
    
    // הוספת אחסון מידע עבור debugging
    this.debugInfo = {
      rays: [],
      intersections: [],
      timings: {
        culling: 0,
        raycast: 0,
        total: 0
      }
    };
    
    // הגדרות אופטימיזציה
    this.enableCulling = options.enableCulling !== undefined ? options.enableCulling : true;
    this.viewportBounds = { x: 0, y: 0, width: 5000, height: 5000 };
    this.cullMargin = options.cullMargin || 200; // שוליים בפיקסלים מחוץ למסך להתחשבות בחישוב
    this.enableDebug = options.enableDebug || false;
    this.useWorker = options.useWorker !== undefined ? options.useWorker : false;
    
    // Web Worker support
    this.worker = null;
    this.pendingRequests = new Map();
    this.requestId = 0;
    
    // Initialize web worker if enabled
    if (this.useWorker) {
      this.initWorker();
    }
    
    // Add cache management fields
    this._cachedVisibility = null;
    this._lastCalculationParams = null;
    this._rayCache = {};
    this._cacheResetScheduled = false;
    this._pendingCacheReset = false;
    this._lastCacheClearTime = 0;
  }

  /**
   * יוזם את העובד לחישובים אסינכרוניים
   * @private
   */
  initWorker() {
    try {
      // בדיקה אם נמצאים בסביבת דפדפן
      if (typeof window !== 'undefined' && window.Worker) {
        const workerCode = `
          // LineBasedFOV Web Worker
          
          // Send a message to the main thread
          function postResponse(requestId, data) {
            self.postMessage({ requestId, ...data });
          }
          
          // Calculate visibility polygon
          function calculateVisibility(origin, radius, walls, bounds, rayCount) {
            // Implementation of visibility calculation
            // This should be a copy of the core FOV calculation logic
            
            // Calculate rays, intersections, etc.
            // ...
            
            // Mock implementation for now - this should be replaced with actual calculation
            const startTime = performance.now();
            
            // Simple polygon for testing
            const visibilityPolygon = [];
            const angles = [];
            
            // Generate angles for rays
            for (let i = 0; i < rayCount; i++) {
              angles.push((i / rayCount) * 2 * Math.PI);
            }
            
            // Perform simplified calculation
            for (const angle of angles) {
              let minT = 1.0;
              let minX = origin.x + Math.cos(angle) * radius;
              let minY = origin.y + Math.sin(angle) * radius;
              
              // Check intersections with all wall segments
              for (const wall of walls) {
                const intersection = calculateIntersection(
                  origin.x, origin.y,
                  origin.x + Math.cos(angle) * radius,
                  origin.y + Math.sin(angle) * radius,
                  wall.x1, wall.y1, wall.x2, wall.y2
                );
                
                if (intersection && intersection.t < minT && intersection.t > 0) {
                  minT = intersection.t;
                  minX = intersection.x;
                  minY = intersection.y;
                }
              }
              
              visibilityPolygon.push(minX, minY);
            }
            
            const endTime = performance.now();
            
            return {
              visibilityPolygon,
              debugInfo: {
                rayCount,
                timings: { total: endTime - startTime }
              }
            };
          }
          
          // Calculate ray-segment intersection
          function calculateIntersection(x1, y1, x2, y2, x3, y3, x4, y4) {
            const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
            
            // Lines are parallel
            if (denominator === 0) {
              return null;
            }
            
            const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
            const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;
            
            // Check if intersection is within both line segments
            if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
              return null;
            }
            
            // Calculate intersection point
            const x = x1 + ua * (x2 - x1);
            const y = y1 + ua * (y2 - y1);
            
            return { x, y, t: ua };
          }
          
          // Handle messages from the main thread
          self.onmessage = function(e) {
            const { requestId, action, data } = e.data;
            
            switch (action) {
              case 'calculate':
                const result = calculateVisibility(
                  data.origin, 
                  data.radius, 
                  data.walls, 
                  data.bounds, 
                  data.rayCount
                );
                postResponse(requestId, { result });
                break;
                
              case 'ping':
                postResponse(requestId, { status: 'ready' });
                break;
                
              default:
                postResponse(requestId, { error: 'Unknown action' });
            }
          };
        `;
        
        // Create a Blob with the worker code
        const blob = new Blob([workerCode], { type: 'application/javascript' });
        const workerUrl = URL.createObjectURL(blob);
        
        // Create the worker
        this.worker = new Worker(workerUrl);
        
        // Set up message handler
        this.worker.onmessage = (e) => {
          const { requestId, result, error } = e.data;
          
          // Find the pending request
          if (this.pendingRequests.has(requestId)) {
            const { resolve, reject } = this.pendingRequests.get(requestId);
            
            if (error) {
              reject(error);
            } else {
              resolve(result);
            }
            
            // Clean up
            this.pendingRequests.delete(requestId);
          }
        };
        
        // Test the worker with a ping
        this.sendToWorker('ping').then(() => {
          // console.log('FOV WebWorker initialized successfully');
        }).catch(err => {
          console.error('Failed to initialize FOV WebWorker:', err);
          this.useWorker = false;
        });
      } else {
        console.warn('Web Workers not supported in this environment');
        this.useWorker = false;
      }
    } catch (err) {
      console.error('Error initializing FOV WebWorker:', err);
      this.useWorker = false;
    }
  }
  
  /**
   * שולח פעולה לביצוע בעובד
   * @param {string} action - סוג הפעולה
   * @param {Object} data - נתוני הפעולה
   * @returns {Promise<any>} תוצאת הפעולה
   * @private
   */
  sendToWorker(action, data = {}) {
    return new Promise((resolve, reject) => {
      if (!this.worker || !this.useWorker) {
        reject(new Error('Worker not available'));
        return;
      }
      
      const requestId = this.requestId++;
      
      // Store the promise callbacks
      this.pendingRequests.set(requestId, { resolve, reject });
      
      // Send the request to the worker
      this.worker.postMessage({ requestId, action, data });
    });
  }

  /**
   * מגדיר את גבולות החלון הנראה לצורך culling
   * @param {Object} bounds - גבולות החלון {x, y, width, height}
   */
  setViewportBounds(bounds) {
    if (bounds && typeof bounds.x === 'number' && typeof bounds.y === 'number' &&
        typeof bounds.width === 'number' && typeof bounds.height === 'number') {
      this.viewportBounds = { ...bounds };
    }
  }

  /**
   * בודק אם קטע קיר מחוץ לגבולות החלון הנראה
   * @param {Object} wall - קטע קיר {x1, y1, x2, y2}
   * @returns {boolean} האם הקטע נמצא מחוץ לחלון הנראה
   */
  isWallOutsideViewport(wall) {
    // אם culling לא מופעל, החזר false (כלומר, התייחס לכל הקירות)
    if (!this.enableCulling) return false;
    
    // הוסף שוליים לגבולות החלון
    const bounds = {
      minX: this.viewportBounds.x - this.cullMargin,
      minY: this.viewportBounds.y - this.cullMargin,
      maxX: this.viewportBounds.x + this.viewportBounds.width + this.cullMargin,
      maxY: this.viewportBounds.y + this.viewportBounds.height + this.cullMargin
    };
    
    // בדוק אם שתי נקודות הקצה של הקטע מחוץ לאותו צד של החלון
    // רק במקרה כזה הקטע בהכרח מחוץ לחלון
    if (wall.x1 < bounds.minX && wall.x2 < bounds.minX) return true;
    if (wall.x1 > bounds.maxX && wall.x2 > bounds.maxX) return true;
    if (wall.y1 < bounds.minY && wall.y2 < bounds.minY) return true;
    if (wall.y1 > bounds.maxY && wall.y2 > bounds.maxY) return true;
    
    return false;
  }

  /**
   * מגדיר את הקטעים החוסמים ראייה
   * @param {Array<{x1:number, y1:number, x2:number, y2:number}>} segments - מערך קטעים
   */
  setWalls(segments) {
    this.walls = [...segments];
  }

  /**
   * מגדיר אובייקטים עם חוקי LOS מיוחדים (למשל, קירות שקופים למחצה)
   * @param {Array<Object>} objects - מערך אובייקטים מיוחדים
   */
  setSpecialObjects(objects) {
    this.specialObjects = [...objects];
  }

  /**
   * מחשב את שדה הראייה מנקודה מסוימת
   * @param {number} x - קואורדינטת X של נקודת המוצא
   * @param {number} y - קואורדינטת Y של נקודת המוצא
   * @param {number} radius - רדיוס הראייה (אופציונלי)
   * @returns {Array<number>} מערך שטוח של נקודות הפוליגון (צורה: [x1,y1,x2,y2,...])
   */
  calculateVisibility(x, y, radius) {
    // const startTime = performance.now(); // Commented: unused debug variable
    
    if (this.cache && this.cache.isValid &&
        this.cache.origin.x === x && this.cache.origin.y === y &&
        this.cache.radius === radius &&
        this.cache.wallsHash === this.getWallsHash() &&
        this.cache.quality === this.rayCount
    ) {
      return [...this.cache.visibilityPolygon];
    }

    this._lastCalculationParams = { x, y, radius };
    this.origin = { x, y };
    const maxDist = radius;
    const visibilityPolygon = [];
    const originalWalls = this.walls ? [...this.walls] : [];
    
    let visibleWalls = this.walls || [];
    let visibleSpecialObjects = this.specialObjects || [];

    // const cullingStartTime = performance.now(); // Commented: unused debug variable
    if (this.enableCulling && this.viewportBounds) {
      const { x: vpX, y: vpY, width: vpWidth, height: vpHeight } = this.viewportBounds;
      const cullMargin = this.cullMargin || 0;
      const extendedBounds = {
        minX: vpX - cullMargin, minY: vpY - cullMargin,
        maxX: vpX + vpWidth + cullMargin, maxY: vpY + vpHeight + cullMargin
      };
      
      visibleWalls = (this.walls || []).filter(wall => 
        this.segmentIntersectsBox(wall.x1, wall.y1, wall.x2, wall.y2, 
                                   extendedBounds.minX, extendedBounds.minY, 
                                   extendedBounds.maxX, extendedBounds.maxY)
      );
      visibleSpecialObjects = (this.specialObjects || []).filter(obj => {
        if (!obj) return false;
        if (obj.shapeType === 'rectangle') {
          return this.segmentIntersectsBox(obj.x, obj.y, obj.x + obj.width, obj.y + obj.height,
                                         extendedBounds.minX, extendedBounds.minY, 
                                         extendedBounds.maxX, extendedBounds.maxY);
        } else if (obj.shapeType === 'circle') {
          return this.circleIntersectsBox(obj.x, obj.y, obj.radius, extendedBounds);
        } else if (obj.shapeType === 'polygon' && obj.points && obj.points.length >= 2) {
          for (let i = 0; i < obj.points.length - 2; i += 2) {
            if (this.segmentIntersectsBox(obj.points[i], obj.points[i+1], obj.points[i+2], obj.points[i+3],
                                           extendedBounds.minX, extendedBounds.minY, 
                                           extendedBounds.maxX, extendedBounds.maxY)) return true;
          }
          if (obj.points.length >=4) { // Check closing segment
             if (this.segmentIntersectsBox(obj.points[obj.points.length-2], obj.points[obj.points.length-1], obj.points[0], obj.points[1],
                                           extendedBounds.minX, extendedBounds.minY, 
                                           extendedBounds.maxX, extendedBounds.maxY)) return true;
          }
          return false;
        }
        return true; // Default: don't cull if type is unknown
      });
    }
    // const cullingEndTime = performance.now(); // Commented: unused debug variable
    
    this.debugInfo = {
      rays: [], intersections: [],
      culledWallsCount: (this.walls || []).length - visibleWalls.length,
      culledSpecialObjectsCount: (this.specialObjects || []).length - visibleSpecialObjects.length,
      processedWallsCount: visibleWalls.length,
      processedSpecialObjectsCount: visibleSpecialObjects.length,
      viewportBounds: { ...this.viewportBounds }
    };
    
    // const raycastStartTime = performance.now(); // Commented: unused debug variable
    
    try {
      for (let i = 0; i < this.rayCount; i++) {
        const angle = (i / this.rayCount) * Math.PI * 2;
        const rayDirX = Math.cos(angle);
        const rayDirY = Math.sin(angle);
        const rayEndX = x + rayDirX * maxDist * 1.5; 
        const rayEndY = y + rayDirY * maxDist * 1.5;
        
        const intersections = [];
        
        // Intersect with standard walls
        for (const wall of visibleWalls) {
          if (!wall || typeof wall.x1 !== 'number') continue; 
          const intersection = this.lineIntersection(x, y, rayEndX, rayEndY, wall.x1, wall.y1, wall.x2, wall.y2);
          if (intersection) {
            const dx = intersection.x - x; const dy = intersection.y - y;
            intersections.push({
              x: intersection.x, y: intersection.y, distSq: dx * dx + dy * dy,
              objectType: 'wall', isBlocking: true 
            });
          }
        }

        // Intersect with special LoS objects
        for (const obj of visibleSpecialObjects) {
            if (!obj || !obj.shapeType || !obj.id) continue;
            let shapeIntersections = []; // Intersections for the current special object

            if (obj.shapeType === 'polygon' && obj.points && obj.points.length >=2) {
                for (let j = 0; j < obj.points.length - 2; j += 2) {
                    const intersect = this.lineIntersection(x, y, rayEndX, rayEndY, obj.points[j], obj.points[j+1], obj.points[j+2], obj.points[j+3]);
                    if (intersect) shapeIntersections.push(intersect);
                }
                if (obj.points.length >= 4) { // Closing segment for polygon
                    const intersect = this.lineIntersection(x, y, rayEndX, rayEndY, obj.points[obj.points.length - 2], obj.points[obj.points.length - 1], obj.points[0], obj.points[1]);
                    if (intersect) shapeIntersections.push(intersect);
                }
            } else if (obj.shapeType === 'rectangle') {
                const { x: rx, y: ry, width: rw, height: rh } = obj;
                const segments = [
                    { x1: rx, y1: ry, x2: rx + rw, y2: ry }, // Top
                    { x1: rx + rw, y1: ry, x2: rx + rw, y2: ry + rh }, // Right
                    { x1: rx + rw, y1: ry + rh, x2: rx, y2: ry + rh }, // Bottom
                    { x1: rx, y1: ry + rh, x2: rx, y2: ry }  // Left
                ];
                for (const seg of segments) {
                    const intersect = this.lineIntersection(x, y, rayEndX, rayEndY, seg.x1, seg.y1, seg.x2, seg.y2);
                    if (intersect) shapeIntersections.push(intersect);
                }
            } else if (obj.shapeType === 'circle') {
                const circleIsects = this.rayCircleIntersection(x, y, rayDirX, rayDirY, obj.x, obj.y, obj.radius);
                shapeIntersections.push(...circleIsects); // Add all intersection points from ray-circle check
            }

            // Add processed intersections for this special object to the main list
            for (const intersect of shapeIntersections) {
                if (!intersect || typeof intersect.x !== 'number' || typeof intersect.y !== 'number') continue;
                const dx = intersect.x - x; const dy = intersect.y - y;
                intersections.push({
                    x: intersect.x, y: intersect.y, distSq: dx * dx + dy * dy,
                    objectType: 'special', objectId: obj.id, isBlocking: false 
                });
            }
        }
        
        // Sort all intersections by distance
        intersections.sort((a, b) => a.distSq - b.distSq);
        
        // Determine final blocking point
        let finalRayEndX = x + rayDirX * maxDist;
        let finalRayEndY = y + rayDirY * maxDist;
        // let rayBlocked = false; // Commented out - debug variable that tracks blocking state but never used in logic
        const specialObjectHits = new Map(); // Tracks hits per special object for the current ray
        
        for (const intersection of intersections) {
          if (intersection.distSq > maxDist * maxDist) break; // Beyond vision radius
          
          if (intersection.objectType === 'wall') { 
            finalRayEndX = intersection.x; finalRayEndY = intersection.y;
            // rayBlocked = true; // Commented out - debug variable not used
            break; // Walls block immediately
          } else if (intersection.objectType === 'special') {
            const objectId = intersection.objectId;
            const currentHits = (specialObjectHits.get(objectId) || 0) + 1;
            specialObjectHits.set(objectId, currentHits);
            if (currentHits >= 2) { // Block on second hit for this special object
              finalRayEndX = intersection.x; finalRayEndY = intersection.y;
              // rayBlocked = true; // Commented out - debug variable not used
              break;
            }
            // First hit: ray passes through
          }
        }
        visibilityPolygon.push(finalRayEndX, finalRayEndY);
      }
    } catch (err) {
      console.error('[LineBasedFOV] Error during ray calculation:', err);
      if (this.visibilityPolygon && this.visibilityPolygon.length > 0) return [...this.visibilityPolygon]; // Fallback
    }
    
    // Close the polygon
    if (visibilityPolygon.length >= 2) {
      visibilityPolygon.push(visibilityPolygon[0], visibilityPolygon[1]);
    }
    
    // Restore original walls if they were somehow modified (safety check)
    if (this.walls && originalWalls && this.walls.length !== originalWalls.length) {
      this.walls = [...originalWalls];
    }
    
    // Update cache
    if (this.cache) {
      this.cache.visibilityPolygon = [...visibilityPolygon];
      this.cache.origin = { x, y }; this.cache.radius = radius;
      this.cache.wallsHash = this.getWallsHash();
      this.cache.quality = this.rayCount; this.cache.isValid = true;
    }
    this.visibilityPolygon = visibilityPolygon; // Store current polygon
    return visibilityPolygon;
  }
  
  /**
   * חישוב נקודות חיתוך בין קרן לאובייקט מיוחד
   * @param {Object} object - האובייקט המיוחד
   * @param {number} x1 - קואורדינטת X של תחילת הקרן
   * @param {number} y1 - קואורדינטת Y של תחילת הקרן
   * @param {number} x2 - קואורדינטת X של סוף הקרן
   * @param {number} y2 - קואורדינטת Y של סוף הקרן
   * @param {number} dirX - כיוון נרמול X של הקרן
   * @param {number} dirY - כיוון נרמול Y של הקרן
   * @param {number} maxDist - המרחק המקסימלי
   * @returns {Array<Object>} מערך נקודות החיתוך
   */
  calculateObjectIntersections(object, x1, y1, x2, y2, dirX, dirY, maxDist) {
    const intersections = [];
    
    // התנהגות לפי סוג האובייקט
    if (object.shapeType === 'polygon' && object.points) {
      // חישוב חיתוך עם פוליגון
      for (let i = 0; i < object.points.length - 2; i += 2) {
        const px1 = object.points[i];
        const py1 = object.points[i + 1];
        const px2 = object.points[i + 2];
        const py2 = object.points[i + 3];
        
        const intersection = this.lineIntersection(x1, y1, x2, y2, px1, py1, px2, py2);
        if (intersection) {
          const dx = intersection.x - x1;
          const dy = intersection.y - y1;
          intersections.push({
            x: intersection.x,
            y: intersection.y,
            distSq: dx * dx + dy * dy
          });
        }
      }
    } else if (object.shapeType === 'rectangle') {
      // חישוב חיתוך עם מלבן
      const { x, y, width, height } = object;
      
      // חישוב 4 צלעות המלבן
      const rectEdges = [
        { x1: x, y1: y, x2: x + width, y2: y }, // צלע עליונה
        { x1: x + width, y1: y, x2: x + width, y2: y + height }, // צלע ימנית
        { x1: x + width, y1: y + height, x2: x, y2: y + height }, // צלע תחתונה
        { x1: x, y1: y + height, x2: x, y2: y } // צלע שמאלית
      ];
      
      // בדיקת חיתוך עם כל צלע
      for (const edge of rectEdges) {
        const intersection = this.lineIntersection(
          x1, y1, x2, y2,
          edge.x1, edge.y1, edge.x2, edge.y2
        );
        
        if (intersection) {
          const dx = intersection.x - x1;
          const dy = intersection.y - y1;
          intersections.push({
            x: intersection.x,
            y: intersection.y,
            distSq: dx * dx + dy * dy
          });
        }
      }
    } else if (object.shapeType === 'circle') {
      // חישוב חיתוך עם עיגול
      const { x: cx, y: cy, radius } = object;
      
      // חישוב נקודות חיתוך בין קרן לעיגול
      const circleIntersections = this.rayCircleIntersection(
        x1, y1, dirX, dirY, cx, cy, radius
      );
      
      for (const intersection of circleIntersections) {
        const dx = intersection.x - x1;
        const dy = intersection.y - y1;
        const distSq = dx * dx + dy * dy;
        
        // התחשב רק בחיתוכים בטווח הרלוונטי
        if (distSq <= (maxDist * 1.5) * (maxDist * 1.5)) {
          intersections.push({
            x: intersection.x,
            y: intersection.y,
            distSq
          });
        }
      }
    }
    
    return intersections;
  }
  
  /**
   * חישוב חיתוך בין שני קטעים ישרים
   * @param {number} x1 - קואורדינטת X של תחילת הקטע הראשון
   * @param {number} y1 - קואורדינטת Y של תחילת הקטע הראשון
   * @param {number} x2 - קואורדינטת X של סוף הקטע הראשון
   * @param {number} y2 - קואורדינטת Y של סוף הקטע הראשון
   * @param {number} x3 - קואורדינטת X של תחילת הקטע השני
   * @param {number} y3 - קואורדינטת Y של תחילת הקטע השני
   * @param {number} x4 - קואורדינטת X של סוף הקטע השני
   * @param {number} y4 - קואורדינטת Y של סוף הקטע השני
   * @returns {Object|null} נקודת החיתוך או null אם אין חיתוך
   */
  lineIntersection(x1, y1, x2, y2, x3, y3, x4, y4) {
    // בדיקת קטעים עם אורך אפס
    if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
      return null;
    }
    
    // חישוב מכנה
    const denominator = ((y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1));
    
    // בדיקת קטעים מקבילים
    if (Math.abs(denominator) < 0.0001) {
      return null;
    }
    
    // חישוב ערכי u
    const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
    const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;
    
    // בדיקה אם החיתוך על שני הקטעים
    if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
      return null;
    }
    
    // חישוב נקודת החיתוך
    const x = x1 + ua * (x2 - x1);
    const y = y1 + ua * (y2 - y1);
    
    return { x, y };
  }
  
  /**
   * חישוב חיתוך בין קרן לעיגול
   * @param {number} rayOriginX - קואורדינטת X של תחילת הקרן
   * @param {number} rayOriginY - קואורדינטת Y של תחילת הקרן
   * @param {number} rayDirX - מרכיב X של וקטור הכיוון של הקרן (מנורמל)
   * @param {number} rayDirY - מרכיב Y של וקטור הכיוון של הקרן (מנורמל)
   * @param {number} circleX - קואורדינטת X של מרכז העיגול
   * @param {number} circleY - קואורדינטת Y של מרכז העיגול
   * @param {number} radius - רדיוס העיגול
   * @returns {Array<Object>} מערך של נקודות חיתוך ממויינות לפי מרחק
   */
  rayCircleIntersection(rayOriginX, rayOriginY, rayDirX, rayDirY, circleX, circleY, radius) {
    const ocX = rayOriginX - circleX; // וקטור ממרכז העיגול לתחילת הקרן
    const ocY = rayOriginY - circleY;
    
    // חישוב מקדמי המשוואה הריבועית
    const a = rayDirX * rayDirX + rayDirY * rayDirY; // אמור להיות 1 אם rayDir מנורמל
    const b = 2 * (ocX * rayDirX + ocY * rayDirY);
    const c = ocX * ocX + ocY * ocY - radius * radius;
    
    // חישוב דיסקרימיננטה
    const discriminant = b * b - 4 * a * c;
    
    if (discriminant < 0) {
      return []; // אין חיתוך
    }
    
    // חישוב ערכי t של נקודות החיתוך
    const t1 = (-b - Math.sqrt(discriminant)) / (2 * a);
    const t2 = (-b + Math.sqrt(discriminant)) / (2 * a);
    
    const intersections = [];
    
    // בדיקה אם נקודות החיתוך בכיוון הקרן
    if (t1 >= 0) {
      intersections.push({
        x: rayOriginX + t1 * rayDirX,
        y: rayOriginY + t1 * rayDirY,
        t: t1
      });
    }
    
    if (discriminant > 0 && t2 >= 0 && t1 !== t2) {
      intersections.push({
        x: rayOriginX + t2 * rayDirX,
        y: rayOriginY + t2 * rayDirY,
        t: t2
      });
    }
    
    // מיון לפי מרחק t
    return intersections.sort((p1, p2) => p1.t - p2.t);
  }
  
  /**
   * מחשב את הנקודות הנדרשות לשרטוט שדה הראייה כפוליגון
   * @returns {Array<number>} מערך שטוח של נקודות [x1,y1,x2,y2,...]
   */
  getVisibilityPolygon() {
    return [...this.visibilityPolygon];
  }
  
  /**
   * בודק אם נקודה נמצאת בתוך שדה הראייה
   * @param {number} x - קואורדינטת X של הנקודה
   * @param {number} y - קואורדינטת Y של הנקודה
   * @returns {boolean} האם הנקודה נראית
   */
  isPointVisible(x, y) {
    // לא ניתן לבדוק אם לא חושב פוליגון הראייה
    if (this.visibilityPolygon.length === 0) {
      return false;
    }
    
    // יצירת פוליגון מסודר (צמדי קואורדינטות)
    const polygon = [];
    for (let i = 0; i < this.visibilityPolygon.length; i += 2) {
      polygon.push({
        x: this.visibilityPolygon[i],
        y: this.visibilityPolygon[i + 1]
      });
    }
    
    // אלגוריתם Ray Casting לבדיקה אם נקודה בתוך פוליגון
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;
      
      const intersect = ((yi > y) !== (yj > y)) &&
        (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      
      if (intersect) inside = !inside;
    }
    
    return inside;
  }
  
  /**
   * בודק אם קטע נמצא (חלקית או במלואו) בתוך שדה הראייה
   * @param {number} x1 - קואורדינטת X של תחילת הקטע
   * @param {number} y1 - קואורדינטת Y של תחילת הקטע
   * @param {number} x2 - קואורדינטת X של סוף הקטע
   * @param {number} y2 - קואורדינטת Y של סוף הקטע
   * @returns {boolean} האם הקטע נראה (לפחות חלקית)
   */
  isSegmentVisible(x1, y1, x2, y2) {
    // בדיקה אם אחת מנקודות הקצה בתוך שדה הראייה
    if (this.isPointVisible(x1, y1) || this.isPointVisible(x2, y2)) {
      return true;
    }
    
    // יצירת פוליגון מסודר
    const polygon = [];
    for (let i = 0; i < this.visibilityPolygon.length; i += 2) {
      polygon.push({
        x: this.visibilityPolygon[i],
        y: this.visibilityPolygon[i + 1]
      });
    }
    
    // בדיקה אם הקטע חוצה את אחת מצלעות הפוליגון
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const intersection = this.lineIntersection(
        x1, y1, x2, y2,
        polygon[i].x, polygon[i].y, polygon[j].x, polygon[j].y
      );
      
      if (intersection) {
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * חישוב שדה הראייה תוך התחשבות במטמון אופציונלי
   * @param {number} x - קואורדינטת X של נקודת המוצא
   * @param {number} y - קואורדינטת Y של נקודת המוצא
   * @param {number} radius - רדיוס הראייה
   * @param {Object} cache - אובייקט מטמון אופציונלי
   * @returns {Array<number>} מערך שטוח של נקודות הפוליגון
   */
  calculateWithCache(x, y, radius, cache) {
    // אם יש מטמון תקף, השתמש בו
    if (cache && cache.isValid && 
        cache.origin.x === x && 
        cache.origin.y === y && 
        cache.radius === radius &&
        cache.wallsHash === this.getWallsHash()) {
      
      this.visibilityPolygon = cache.visibilityPolygon;
      return this.visibilityPolygon;
    }
    
    // אחרת, חשב מחדש
    const result = this.calculateVisibility(x, y, radius);
    
    // שמור במטמון אם סופק
    if (cache) {
      cache.visibilityPolygon = [...result];
      cache.origin = { x, y };
      cache.radius = radius;
      cache.wallsHash = this.getWallsHash();
      cache.isValid = true;
    }
    
    return result;
  }
  
  /**
   * יוצר גיבוב (hash) של מערך הקירות הנוכחי
   * משמש לזיהוי שינויים במבנה הקירות עבור מטמון
   * @returns {string} מחרוזת גיבוב
   */
  getWallsHash() {
    // שימוש בפונקציית גיבוב פשוטה
    let hash = 0;
    for (const wall of this.walls) {
      const str = `${wall.x1},${wall.y1},${wall.x2},${wall.y2}`;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // המרה ל-32 סיביות
      }
    }
    return hash.toString();
  }

  /**
   * מחזיר מידע דיבאג על הקרניים והחיתוכים לצורך ויזואליזציה
   * @returns {Object} אובייקט עם מידע על הקרניים והחיתוכים
   */
  getRaycastDebugInfo() {
    if (!this.enableDebug) {
      return null;
    }
    
    return this.debugInfo;
  }

  /**
   * שחרור משאבים
   */
  dispose() {
    // שחרור העובד אם קיים
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    
    // ניקוי מטמונים וחשבונות אחרים
    this.pendingRequests.clear();
    this.walls = [];
    this.specialObjects = [];
    this.visibilityPolygon = [];
  }

  /**
   * נקה את כל המטמונים במערכת באופן מיידי
   * פונקציה זו נקראת כדי להבטיח שחישוב ה-FOV הבא יתבסס על נתונים עדכניים לחלוטין.
   */
  clearCache() {
    // console.log('[LineBasedFOV] clearCache called - performing immediate comprehensive reset.');
    
    // איפוס מטמוני תוצאות החישוב
      this._cachedVisibility = null;
    this._lastCalculationParams = null; // פרמטרים של החישוב האחרון
    this.visibilityPolygon = [];      // הפוליגון המחושב האחרון
    
    // איפוס מטמונים פנימיים של האלגוריתם
    this._rayCache = {}; // מטמון לקרניים או לתוצאות ביניים של קרניים
    // אם יש עוד מטמונים ספציפיים לאלגוריתם, יש לאפס גם אותם.
    // לדוגמה, אם היה מטמון גיאומטריות מעובדות:
    // this._geometryCache = null; 
      
      // איפוס מידע דיבוג
    this.debugInfo = {
        rays: [],
        intersections: [],
      timings: {
        culling: 0,
        raycast: 0,
        total: 0
      },
      culledWallsCount: 0,
      culledSpecialObjectsCount: 0,
      processedWallsCount: (this.walls || []).length,
      processedSpecialObjectsCount: (this.specialObjects || []).length,
      viewportBounds: { ...(this.viewportBounds || {}) }
    };
    
    // אין צורך בניהול דגלי pending/scheduled אם האיפוס הוא מיידי
    // this._pendingCacheReset = false;
    // this._cacheResetScheduled = false;
      
    // console.log('[LineBasedFOV] Cache cleared immediately.');
    return this;
  }

  // Helper function to check if a segment intersects with a box
  segmentIntersectsBox(x1, y1, x2, y2, minX, minY, maxX, maxY) {
    // Check if either endpoint is inside the box
    if ((x1 >= minX && x1 <= maxX && y1 >= minY && y1 <= maxY) ||
        (x2 >= minX && x2 <= maxX && y2 >= minY && y2 <= maxY)) {
      return true;
    }
    
    // Check if the segment intersects any of the box edges
    if (this.lineIntersection(x1, y1, x2, y2, minX, minY, maxX, minY) || // Top edge
        this.lineIntersection(x1, y1, x2, y2, maxX, minY, maxX, maxY) || // Right edge
        this.lineIntersection(x1, y1, x2, y2, maxX, maxY, minX, maxY) || // Bottom edge
        this.lineIntersection(x1, y1, x2, y2, minX, maxY, minX, minY)) { // Left edge
      return true;
    }
    
    return false;
  }
  
  // Helper function to check if a circle intersects with a box
  circleIntersectsBox(circleX, circleY, radius, bounds) {
    const { minX, minY, maxX, maxY } = bounds;
    
    // Find the closest point on the box to the circle center
    const closestX = Math.max(minX, Math.min(circleX, maxX));
    const closestY = Math.max(minY, Math.min(circleY, maxY));
    
    // Calculate the distance from the closest point to the circle center
    const distanceX = circleX - closestX;
    const distanceY = circleY - closestY;
    const distanceSquared = distanceX * distanceX + distanceY * distanceY;
    
    // If the distance is less than the radius, the circle intersects the box
    return distanceSquared <= (radius * radius);
  }
}

export default LineBasedFOV; 